import React, { useState, useEffect, forwardRef } from 'react';
import { Status, getBatchEditValue } from '../helpers';
import { Checkbox, Input, Select, SelectButtons, TextareaAutosize } from '../components';
import { useMessage } from '../hooks';

const _AddStatusForm = ({ mode, data, submitForm, loading, setLoading, formErrors = {} }, ref) => {
  const initialFormState = {
    status_id: null,
    status_name: '',
    status_description: '',
    status_stage: null,
    status_asset_category: null,
    status_is_default: false,
  };

  const initialBatchEditItems = {
    batch_status_stage: false,
    batch_status_asset_category: false,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);
  const { hideMessage } = useMessage();

  useEffect(() => {
    setBatchEdit(false);
    if (data.length === 1) {
      setFormData({
        status_id: data[0].status_id,
        status_name: data[0].status_name || '',
        status_description: data[0].status_description || '',
        status_stage: data[0].status_stage,
        status_asset_category: data[0].status_asset_category,
        status_is_default: data[0].status_is_default || false,
      });
    } else if (data.length > 1 && mode !== 'add') {
      setBatchEdit(true);

      setFormData({
        status_stage: getBatchEditValue(data, 'status_stage'),
        status_asset_category: getBatchEditValue(data, 'status_asset_category'),
      });
      //} else if(mode === 'add') {
    } else {
      resetForm();
    }
  }, [mode, data]);

  const handleInputChange = (event, formPart) => {
    let { name, value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      default:
        setFormData({ ...formData, [name]: value });
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  const onSubmit = event => {
    event.preventDefault();

    setLoading(true);

    // Form validation
    //if (!formData.user_fullname || !formData.user_username) return

    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }

      dataToSubmit = data.map(item => {
        return {
          status_id: item.status_id,
          status_name: item.status_name,
          status_stage: item.status_stage,
          status_description: item.status_description,
          status_is_default: item.status_is_default,
          status_asset_category: item.status_asset_category,
          ...tmp,
        };
      });
    } else {
      dataToSubmit = formData;
    }

    // submit form
    submitForm(dataToSubmit)
      .then(() => resetForm())
      .catch(() => {
        // Errors handled in submitForm function
      })
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);
    setFormData(prevFormData => {
      return { ...initialFormState };
    });
  };

  const statusStages = Object.entries(Status).map(([key, val]) => ({
    value: key.toString(),
    label: val.name,
    tooltip: `Asset lifecycle stage ${key}`,
    className: val.className,
  }));

  const assetCategories = [
    {
      value: 'glossary',
      label: 'Glossary',
      tooltip: `This status will be available for glossaries, entities, attributes and attribute values`,
    },
    {
      value: 'system',
      label: 'System',
      tooltip: `This status will be available for systems, datasources, dataset groups, datasets and fields`,
    },
  ];

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        {!batchEdit && (
          <div className="form-block vertical">
            <Input
              type="text"
              name="status_name"
              label="Name"
              value={formData.status_name}
              onChange={handleInputChange}
              disabled={loading}
              validation={
                formErrors.status_name
                  ? { valid: false, message: formErrors.status_name }
                  : undefined
              }
            />
          </div>
        )}
        {!batchEdit && (
          <div className="form-block vertical">
            <TextareaAutosize
              name="status_description"
              label="Description"
              value={formData.status_description}
              onChange={handleInputChange}
              disabled={loading}
              isOptional={true}
            />
          </div>
        )}

        <div className="form-block vertical">
          {batchEdit ? (
            <label>
              <Checkbox
                checked={batchEditItems.batch_status_stage}
                name="batch_status_stage"
                label="Edit Lifecycle Stage"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          ) : null}

          <Select
            name="status_stage"
            label={!batchEdit && 'Lifecycle Stage'}
            options={statusStages}
            value={formData.status_stage === 'multiple' ? undefined : formData.status_stage}
            onChange={handleInputChange}
            sortOptions={false}
            title="Define the asset lifecycle stage, which sets the visual appearance in the GUI"
            placeholder={
              formData.status_stage === 'multiple' ? '< Multiple >' : 'Select lifecycle stage...'
            }
            disabled={loading || (batchEdit && !batchEditItems.batch_status_stage)}
            validation={
              formErrors.status_stage
                ? { valid: false, message: formErrors.status_stage }
                : undefined
            }
          />
        </div>

        <div className="form-block vertical">
          {batchEdit ? (
            <label>
              <Checkbox
                checked={batchEditItems.batch_status_asset_category}
                name="batch_status_asset_category"
                label="Edit Asset Category"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          ) : null}

          <div className="horizontal">
            <SelectButtons
              name="status_asset_category"
              options={assetCategories}
              value={formData.status_asset_category}
              onChange={handleInputChange}
              label={!batchEdit && 'Asset Category'}
              title="The category of assets the status applies to"
              disabled={loading || (batchEdit && !batchEditItems.batch_status_asset_category)}
              validation={
                formErrors.status_asset_category
                  ? { valid: false, message: formErrors.status_asset_category }
                  : undefined
              }
            />
          </div>
        </div>

        {!batchEdit && (
          <div className="form-block vertical">
            <div className="horizontal">
              <Checkbox
                name="status_is_default"
                checked={formData.status_is_default}
                onChange={handleInputChange}
                label="Is Default"
                title="Set this as the default status when creating new assets"
                disabled={loading}
              />
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export const AddStatusForm = forwardRef(_AddStatusForm);
